import React, { useRef, useState, useEffect, useCallback } from 'react';
import { FaCloudUploadAlt, FaTimesCircle } from 'react-icons/fa';
import { FormattedMessage } from 'react-intl';
import { useAuth } from '@/hooks/useAuth';
import { useGetAgent } from '@/hooks/useAgent';
import { useUploadBotIcon } from '@/hooks/useUploadFile';
import { Modal } from 'antd';
import { useIntl } from 'react-intl';
import { UpdateAgentMutation } from '../../../../../../hooks/useAgentBuilder';

const AvatarUpload = () => {
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);

  const intl = useIntl();
  const {userData} = useAuth()
  const { data: agent, isLoading } = useGetAgent(userData?.uID);
  const uploadBotIcon = useUploadBotIcon();
  const updateAgentMutation = UpdateAgentMutation()

  // Set ảnh mặc định từ agent.bot_icon nếu chưa có previewUrl và chưa bị xoá
  useEffect(() => {
    if (agent?.data?.bot_icon && !previewUrl && !isRemoved) {
      setPreviewUrl(agent?.data?.bot_icon);
    }
  }, [agent?.data?.bot_icon, previewUrl, isRemoved]);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        Modal.error({
          title: intl.formatMessage({ id: "AGENTS.HOME.INVALID_FILE_TYPE" }),
          content: intl.formatMessage({ id: "AGENTS.HOME.INVALID_FILE_TYPE_MESSAGE" }),
          okText: intl.formatMessage({ id: "AGENTS.HOME.OK" }),
        });
        return;
      }

      // Validate file size (2048KB = 2MB)
      const maxSize = 2048 * 1024; // 2048KB in bytes
      if (file.size > maxSize) {
        Modal.error({
          title: intl.formatMessage({ id: "AGENTS.HOME.FILE_TOO_LARGE" }),
          content: intl.formatMessage({ id: "AGENTS.HOME.FILE_TOO_LARGE_MESSAGE" }),
          okText: intl.formatMessage({ id: "AGENTS.HOME.OK" }),
        });
        return;
      }

      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setHovered(false); // RESET trạng thái hover
      setIsRemoved(false);
      Modal.confirm({
        title: intl.formatMessage({ id: "AGENTS.HOME.CONFIRM_CHANGE_TITLE" }),
        content: intl.formatMessage({ id: "AGENTS.HOME.CONFIRM_CHANGE_CONTENT" }),
        okText: intl.formatMessage({ id: "AGENTS.HOME.CONFIRM" }),
        cancelText: intl.formatMessage({ id: "AGENTS.HOME.CANCEL" }),
        okButtonProps: { 
          style: { backgroundColor: '#50b553', borderColor: '#50b553' },
          className: 'bg-[#50b553] border-[#50b553] hover:bg-[#50b553] hover:border-[#50b553]'
        },
        onOk() {
          uploadBotIcon.mutate(
            { file },
            {
              onError: () => {
                // Clear failed preview and reset input so the same file can be re-selected
                if (previewUrl) {
                  try { URL.revokeObjectURL(url); } catch (_) {}
                }
                setPreviewUrl(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              },
            }
          );
        },
        onCancel() {
          // Clear preview when user cancels
          try { URL.revokeObjectURL(url); } catch (_) {}
          setPreviewUrl(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        },
      });
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        Modal.error({
          title: intl.formatMessage({ id: "AGENTS.HOME.INVALID_FILE_TYPE" }),
          content: intl.formatMessage({ id: "AGENTS.HOME.INVALID_FILE_TYPE_MESSAGE" }),
          okText: intl.formatMessage({ id: "AGENTS.HOME.OK" }),
        });
        return;
      }

      // Validate file size (2048KB = 2MB)
      const maxSize = 2048 * 1024; // 2048KB in bytes
      if (file.size > maxSize) {
        Modal.error({
          title: intl.formatMessage({ id: "AGENTS.HOME.FILE_TOO_LARGE" }),
          content: intl.formatMessage({ id: "AGENTS.HOME.FILE_TOO_LARGE_MESSAGE" }),
          okText: intl.formatMessage({ id: "AGENTS.HOME.OK" }),
        });
        return;
      }

      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setIsRemoved(false);
      // Xác nhận trước khi upload bằng Modal.confirm
      Modal.confirm({
        title: intl.formatMessage({ id: "AGENTS.HOME.CONFIRM_CHANGE_TITLE" }),
        content: intl.formatMessage({ id: "AGENTS.HOME.CONFIRM_CHANGE_CONTENT" }),
        okText: intl.formatMessage({ id: "AGENTS.HOME.CONFIRM" }),
        cancelText: intl.formatMessage({ id: "AGENTS.HOME.CANCEL" }),
        okButtonProps: { 
          style: { backgroundColor: '#50b553', borderColor: '#50b553' },
          className: 'bg-[#50b553] border-[#50b553] hover:bg-[#50b553] hover:border-[#50b553]'
        },
        onOk() {
          uploadBotIcon.mutate(
            { file },
            {
              onError: () => {
                // Clear failed preview for drag-and-drop path
                if (previewUrl) {
                  try { URL.revokeObjectURL(url); } catch (_) {}
                }
                setPreviewUrl(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              },
            }
          );
        },
        onCancel() {
          // Clear preview when user cancels
          try { URL.revokeObjectURL(url); } catch (_) {}
          setPreviewUrl(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        },
      });
    }
  }, [userData?.uID, uploadBotIcon, intl]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleRemove = (e) => {
    e.stopPropagation();

    Modal.confirm({
      title: intl.formatMessage({ id: 'AGENTS.HOME.CONFIRM_CHANGE_TITLE' }),
      content: intl.formatMessage({ id: 'AGENTS.HOME.CONFIRM_CHANGE_CONTENT' }),
      okText: intl.formatMessage({ id: 'AGENTS.HOME.CONFIRM' }),
      cancelText: intl.formatMessage({ id: 'AGENTS.HOME.CANCEL' }),
      okButtonProps: { 
        style: { backgroundColor: '#50b553', borderColor: '#50b553' },
        className: 'bg-[#50b553] border-[#50b553] hover:bg-[#50b553] hover:border-[#50b553]'
      },
      onOk: () => {
        // Xoá ảnh preview hiện tại sau khi xác nhận
        setPreviewUrl(null);
        setIsRemoved(true);

        // Reset input để có thể chọn lại ảnh vừa xoá
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        // Gọi API cập nhật để xoá bot_icon trên server
        updateAgentMutation.mutate({ bot_icon: null });
      },
    });
  };

  return (
    <div className="flex flex-col items-center mb-4">
      <div
        className={`w-full max-w-md border-2 border-dashed border-gray-300 rounded-lg bg-white flex flex-col items-center justify-center p-4 cursor-pointer`}
        onClick={!previewUrl ? handleButtonClick : undefined}
        onDrop={!previewUrl ? handleDrop : undefined}
        onDragOver={!previewUrl ? handleDragOver : undefined}
        onDragLeave={!previewUrl ? handleDragLeave : undefined}
        style={{ minHeight: 120 }}
      >
        {previewUrl ? (
          <div
            className="relative rounded-full bg-white mb-2 size-20 flex items-center justify-center overflow-hidden"
            style={{ boxShadow: '0 0 12px 0 rgba(0,0,0,0.12)' }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <img
              src={previewUrl}
              alt="Avatar Preview"
              className={`object-cover w-full h-full transition duration-200 ${hovered ? 'opacity-50' : ''}`}
            />
            {hovered && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/50">
                <button
                  type="button"
                  onClick={handleRemove}
                  className="rounded-full p-2 text-red-500 hover:bg-gray-100 transition shadow"
                  title={<FormattedMessage id="AGENTS.HOME.REMOVE_IMAGE" />}
                >
                  <FaTimesCircle size={32} />
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div
              className="p-2 rounded bg-white mb-2"
              style={{ boxShadow: '0 0 12px 0 rgba(0,0,0,0.12)', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
            >
              <FaCloudUploadAlt className="size-6 text-[#B3B3B3]" />
            </div>
            <div className='flex items-center gap-1 text-xs'>
              <span className="text-green-600 font-semibold cursor-pointer"><FormattedMessage id="AGENTS.HOME.CLICK_TO_UPLOAD" /></span>
              <span className="text-gray-500"><FormattedMessage id="AGENTS.HOME.DRAG_AND_DROP" /></span>
            </div>
            <span className="text-gray-400 mt-1 text-xs"><FormattedMessage id="AGENTS.HOME.UPLOAD_FORMAT" /></span>
          </>
        )}

        {/* Luôn giữ input tồn tại để trigger lại khi click */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".jpeg,.png,.jpg,.gif,.svg"
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
};

export { AvatarUpload };
